import {
    takeEvery,
    all,
    put,
    call,
    select,
    fork
} from 'redux-saga/effects';
import fp from 'lodash/fp';
import { push, replace, LOCATION_CHANGE } from 'connected-react-router';
import urlJoin from 'url-join';
import queryString from 'query-string';
import moment from 'moment';

import actionFactory from '../../webcommon/header/filterheader/actions';
import * as filterHeaderSelectors from '../../webcommon/header/filterheader/selectors';

import * as csaSummaryApi from '../../api/csaSummaryApi';
import * as actions from './actions';
import * as selectors from './selectors';
import * as routerSelectors from '../../common/router/selectors';
import {
    searchToFilters, filtersToSearch, urlToSort, encodeSortingIntoUrl
} from '../../common/urlUtils';
import * as appSelectors from '../../app/selectors';
import * as configurationApi from '../../api/configurationApi';
import * as systemInfoApi from '../../api/systemInfoApi';
import { restructureListByType } from '../../api/adapters/filterAdapters';
import * as appConstants from '../../common/constants';
import * as appAdapters from '../../api/adapters/systemInfoAdapter';
import * as appActions from '../../app/actions';

import { getVirtualDirectory } from '../../common/helpermethods/HelperMethods';

export const PAGE_ROUTE = urlJoin(getVirtualDirectory(), '/summary/csasummary');

const filterHeaderActions = actionFactory('CSASummary');

export function* setAvailableCsasByCounterparty(counterparties = []) {
    const potentialCsaValues = yield select(appSelectors.getImCsas);
    const allowedCsas = counterparties && counterparties.length
        ? potentialCsaValues.filter(csa => counterparties.includes(csa.counterparty))
        : potentialCsaValues;

    const csas = restructureListByType(allowedCsas);

    yield put(filterHeaderActions.setAvailableListValues('csa', csas));

    const activeFilters = yield select(filterHeaderSelectors.getFilters, 'CSASummary');
    const csaFilterIndex = fp.findIndex(filter => filter.definition.id === 'csa', activeFilters);
    const csaFilter = activeFilters[csaFilterIndex];

    if (Array.isArray(csaFilter.value) && csaFilter.value.length) {
        const filteredCsaFilterValue = csaFilter
            .value.filter(csaId => allowedCsas.find(csa => csa.value === csaId));
        const newFilters = fp.set(`[${csaFilterIndex}].value`, filteredCsaFilterValue, activeFilters);
        const openFilter = yield select(filterHeaderSelectors.getCurrentlyOpenFilter, 'CSASummary');
        yield put(filterHeaderActions.setFilters(newFilters));
        if (openFilter) {
            yield put(filterHeaderActions.openFilter(openFilter));
        }
    }
}

export function* filterChanged(forceUpdate) {
    const initialised = yield select(selectors.getInitialised);
    if (initialised) {
        const currentlyOpenFilter = yield (select(filterHeaderSelectors.getCurrentlyOpenFilter, 'CSASummary'));
        if (!currentlyOpenFilter) {
            const filters = yield select(filterHeaderSelectors.getFilters, 'CSASummary');
            const gridFiltering = yield select(selectors.getDefaultSort);
            const filterSearch = filtersToSearch(filters);
            const gridSearch = encodeSortingIntoUrl(gridFiltering);
            const search = `${filterSearch}&${gridSearch}`;
            const sp = queryString.parse(search);
            let force = false;
            if (forceUpdate) {
                sp.force = 'true';
                force = true;
            }
            const newSearch = `?${queryString.stringify(sp)}`;
            const currentSearch = yield select(routerSelectors.getSearch);
            if (force || !currentSearch) {
                yield put(replace(`${PAGE_ROUTE}${newSearch}`));
            } else if (currentSearch !== newSearch) {
                yield put(push(`${PAGE_ROUTE}${newSearch}`));
            }
        }
    }
}

export function* getCsas() {
    const filters = yield select(filterHeaderSelectors.getFilters, 'CSASummary');
    const counterpartyFilter = filters.find(filter => filter.definition.id === 'counterparty');
    yield call(setAvailableCsasByCounterparty, counterpartyFilter.value);
}

export function* getCounterparties() {
    const counterpartyList = yield select(appSelectors.getImCounterparties);
    const counterparties = restructureListByType(counterpartyList);
    yield put(filterHeaderActions.setAvailableListValues('counterparty', counterparties));
}

export function* getAvailableValues(action) {
    switch (action.filterId) {
        case 'csa':
            yield call(getCsas);
            break;
        case 'counterparty':
            yield call(getCounterparties);
            break;
        default:
    }
}

export function* handleUpdate(action) {
    const initialised = yield select(selectors.getInitialised);
    if (initialised) {
        if (action.filterId === 'runDate') {
            try {
                const EodDateStart = moment(action.value.from).format(appConstants.dateFormat);
                const EodDateEnd = moment(action.value.to).format(appConstants.dateFormat);
                const csas = yield call(systemInfoApi.csasByEodRange, { RunStartDate: EodDateStart, RunEndDate: EodDateEnd });
                const csaList = appAdapters.createCsaList(csas);
                yield put(appActions.fetchCsaDataSucceeded(csaList));
                if (!csaList.length) {
                    yield put(appActions.fetchCsaDataEmpty('The CSA list was empty for the chosen date'));
                }
                yield call(getCounterparties);
                yield call(getCsas);
            } catch (error) {
                yield put(appActions.fetchCsaDataFailed(error.message));
            }
        }
        if (action.filterId === 'counterparty') {
            yield call(setAvailableCsasByCounterparty, action.value);
        } else if (action.type === filterHeaderActions.RESET_FILTERS) {
            const filters = yield select(filterHeaderSelectors.getFilters, 'CSASummary');
            const EodDateStart = moment(filters[0].value.from).format(appConstants.dateFormat);
            const EodDateEnd = moment(filters[0].value.to).format(appConstants.dateFormat);
            const csas = yield call(systemInfoApi.csasByEodRange, { RunStartDate: EodDateStart, RunEndDate: EodDateEnd });
            const csaList = appAdapters.createCsaList(csas);
            yield put(appActions.fetchCsaDataSucceeded(csaList));
            if (!csaList.length) {
                yield put(appActions.fetchCsaDataEmpty('The CSA list was empty for the chosen date'));
            }
            yield call(getCounterparties);
            yield call(getCsas);
            const counterpartyFilterValue = fp.flow(
                fp.find(filter => fp.get('definition.id', filter) === 'counterparty'),
                fp.get('value')
            )(filters);
            yield call(setAvailableCsasByCounterparty, counterpartyFilterValue);
        }

        yield call(filterChanged, false);
    }
}

export function* loadDefaultConfiguration(api) {
    try {
        const config = yield call(api.getConfiguration, 'CSASummary');
        if (config && config.columns) {
            yield put(actions.setDefaultConfig(config.columns));
        }
    } catch (error) {
        yield put(actions.fetchDefaultConfigFailed(error.message));
    }
}

export function* csaSummariesLoaded(configApi) {
    try {
        const initialised = yield (select(selectors.getInitialised));
        if (!initialised) {
            yield fork(loadDefaultConfiguration, configApi);
            yield put(actions.setLoading(true));
            const filterDefinitions = yield select(selectors.getFilterDefinitions);
            yield put(filterHeaderActions.setFilterDefinitions(filterDefinitions));
            const defaultFilters = yield select(selectors.getDefaultFilters);
            const availableEOD = yield select(appSelectors.getAvailableEOD);
            const filters = fp.set('[0].value', availableEOD)(defaultFilters);
            const csas = yield call(systemInfoApi.csasByEodRange, { RunStartDate: availableEOD.from, RunEndDate: availableEOD.to });
            const csaList = appAdapters.createCsaList(csas);
            yield put(appActions.fetchCsaDataSucceeded(csaList));
            if (!csaList.length) {
                yield put(appActions.fetchCsaDataEmpty('The CSA list was empty for the chosen date'));
            }
            yield put(filterHeaderActions.setDefaultFilters(filters));
            const search = yield select(routerSelectors.getSearch);
            if (!search) {
                yield put(filterHeaderActions.resetFilters());
            } else {
                const filters = searchToFilters(search, filterDefinitions, defaultFilters);
                yield put(actions.setAllGridSorts(urlToSort(search)));
                yield put(filterHeaderActions.setFilters(filters));
            }
            yield put(actions.setInitialised(true));
            yield call(filterChanged, true);
        }
    } finally {
        yield put(actions.setLoading(false));
    }
}

export function* handleLocationChangedWithoutQuerystring() {
    const initialised = yield (select(selectors.getInitialised));
    if (initialised) {
        yield call(filterChanged, false);
    }
}

export function* handleLocationChanged(api, action) {
    const initialised = yield (select(selectors.getInitialised));
    if (initialised) {
        try {
            const filterDefinitions = yield select(selectors.getFilterDefinitions);
            const defaultFilters = yield select(selectors.getDefaultFilters);
            const filters = searchToFilters(
                action.payload.location.search,
                filterDefinitions,
                defaultFilters
            );
            yield put(filterHeaderActions.setFilters(filters));
            yield put(actions.setAllGridSorts(urlToSort(action.payload.location.search)));
            const params = queryString.parse(action.payload.location.search);
            const force = params.force === 'true';

            const requestedServerFilters = fp.flow(
                fp.filter(filter => filter.value && filter.definition.server),
                fp.sortBy(filter => filter.definition.id),
                JSON.stringify
            )(filters);
            const currentServerFilters = yield select(selectors.getCurrentServerFilters);
            if (force || (currentServerFilters !== requestedServerFilters)) {
                yield put(actions.setLoading(true));
                const data = yield call(api.csaSummaries, filters);
                yield put(actions.fetchCsaSummariesSucceeded(
                    data,
                    requestedServerFilters
                ));
                if (data.length === 0) {
                    yield put(actions.fetchCsaSummariesEmpty('No CSA Summaries data present'));
                }
            }
            const requestedClientFilters = fp.flow(
                fp.filter(filter => filter.value && !filter.definition.server),
                fp.sortBy(filter => filter.definition.id)
            )(filters);

            // either the server filters were changed, so we need to apply client
            // filters, or the client filters changed. Either way, we always need
            // to apply the client filter.
            yield put(actions.applyClientFilters(requestedClientFilters));
        } catch (error) {
            yield put(actions.fetchCsaSummariesFailed(error.message));
        } finally {
            yield put(actions.setLoading(false));
        }
    }
}

export function* openFilter(filter) {
    if (!filter.filterId) {
        yield call(filterChanged, false);
    }
}

export function* refreshCsaSummary(api) {
    try{
        const filters = yield select(filterHeaderSelectors.getFilters, 'CSASummary');
        const requestedServerFilters = fp.flow(
            fp.filter(filter => filter.value && filter.definition.server),
            fp.sortBy(filter => filter.definition.id),
            JSON.stringify
        )(filters);
        yield put(actions.setLoading(true));
        const data = yield call(api.csaSummaries, filters);
        yield put(actions.fetchCsaSummariesSucceeded(
            data,
            requestedServerFilters
        ));
        if (data.length === 0) {
            yield put(actions.fetchCsaSummariesEmpty('No CSA Summaries data present'));
        }
        const requestedClientFilters = fp.flow(
            fp.filter(filter => filter.value && !filter.definition.server),
            fp.sortBy(filter => filter.definition.id)
        )(filters);

        // either the server filters were changed, so we need to apply client
        // filters, or the client filters changed. Either way, we always need
        // to apply the client filter.
        yield put(actions.applyClientFilters(requestedClientFilters));
    }
    catch (error){
        yield put(actions.fetchCsaSummariesFailed(error.message));
    } finally {
        yield put(actions.setLoading(false));
    }
}

const testLocation = emptySearch => (action) => {
    if (action.type !== LOCATION_CHANGE) {
        return false;
    }
    if (action.payload.location.pathname.toLowerCase() === PAGE_ROUTE.toLowerCase()) {
        return !!action.payload.location.search !== emptySearch;
    }
    return false;
};

export default function* rootSaga() {
    yield all([
        takeEvery(actions.CSASUMMARIES_LOADED, csaSummariesLoaded, configurationApi),
        takeEvery([
            filterHeaderActions.UPDATE_FILTER,
            filterHeaderActions.REMOVE_FILTER,
            filterHeaderActions.RESET_FILTERS
        ], handleUpdate),
        takeEvery([
            actions.REFRESH_DATA
        ], filterChanged, true),
        takeEvery(
            filterHeaderActions.FETCH_AVAILABLE_LIST_VALUES,
            getAvailableValues,
        ),
        takeEvery(
            testLocation(false),
            handleLocationChanged,
            csaSummaryApi
        ),
        takeEvery(
            testLocation(true),
            handleLocationChangedWithoutQuerystring,
            csaSummaryApi
        ),
        takeEvery(filterHeaderActions.OPEN_FILTER, openFilter),
        takeEvery(actions.SET_GRID_SORTS, filterChanged, false),
        takeEvery(actions.REFRESH_CSASUMMARIES, refreshCsaSummary, csaSummaryApi)
    ]);
}
